import React, { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Button, Checkbox, Divider, Dropdown, Popover, Tag, Tooltip } from "antd"
import { MoreOutlined, PlusOutlined } from "@ant-design/icons"
import { capitalizeFirstLetter } from "utils/data-formatters"
import styles from "./useDetailsColumns.module.css"

const PAYMENT_METHOD_COLOR = {
  card: "blue",
  cash: "green",
  check: "purple",
  bank_transfer: "cyan",
  online: "blue",
  stripe: "blue",
  paypal: "blue"
}

// Default visible columns — transaction_id and booking_source are excluded from defaults
const DEFAULT_VISIBLE_KEYS = ["date", "displayed_name", "payment_method", "amount", "base_cost", "booking_total_amount"]

// All possible sales column keys in display order
const ALL_SALES_COLUMN_KEYS = [
  "date",
  "transaction_id",
  "booking_number",
  "displayed_name",
  "payment_method",
  "booking_source",
  "amount",
  "taxes_and_fees",
  "booking_total_amount",
  "base_cost",
  "first_experience_date",
  "last_experience_date"
]

// Columns that cannot be hidden (always locked on)
const LOCKED_COLUMN_KEYS = new Set(DEFAULT_VISIBLE_KEYS)

export const formatCurrency = (value) => `$${Number.parseFloat(value || 0).toFixed(2)}`
export const makeOptions = (values) =>
  Array.from(new Set(values.filter(Boolean))).map((value) => ({ label: capitalizeFirstLetter(value), value }))

// Allow sort only when clicking the sorter icon, not the whole header cell
const sortIconOnlyHeaderCell = () => ({
  onClick: (e) => {
    if (!e.target.closest(".ant-table-column-sorter")) e.stopPropagation()
  }
})

function ColumnHeader({ label, tooltip }) {
  return (
    <Tooltip title={tooltip || label}>
      <span>{label}</span>
    </Tooltip>
  )
}

ColumnHeader.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string
}

function formatAmount(record) {
  const raw = Number(record.amount || 0)
  const isRefund = raw < 0
  const display = record.formatted_amount || formatCurrency(Math.abs(raw))
  if (isRefund) {
    return <span className={styles.refundAmount}>{`(${display})`}</span>
  }
  return <span>{display}</span>
}

function baseSalesColumns(t) {
  return [
    {
      title: <ColumnHeader label={t("accounting.details.columns.transaction_date")} />,
      dataIndex: "date",
      key: "date",
      width: 180,
      render: (d) => d || "-",
      sorter: (a, b) => new Date(a.date || 0) - new Date(b.date || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.transaction_id")} />,
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 180,
      ellipsis: true,
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <div className={styles.truncatedCell}>{v}</div>
          </Tooltip>
        ) : (
          "-"
        ),
      sorter: (a, b) => (a.transaction_id || "").localeCompare(b.transaction_id || ""),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.booking_id")} />,
      dataIndex: "booking_number",
      key: "booking_number",
      width: 130,
      sorter: (a, b) => (a.booking_number || 0) - (b.booking_number || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.client_name")} />,
      dataIndex: "displayed_name",
      key: "displayed_name",

      width: 160,
      sorter: (a, b) => (a.displayed_name || "").localeCompare(b.displayed_name || ""),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.payment_method")} />,
      dataIndex: "payment_method",
      key: "payment_method",
      width: 180,
      render: (v) =>
        v ? (
          <Tag color={PAYMENT_METHOD_COLOR[v?.toLowerCase()] || "default"} className={styles.tag}>
            {capitalizeFirstLetter(v)}
          </Tag>
        ) : (
          "-"
        ),
      sorter: (a, b) => (a.payment_method || "").localeCompare(b.payment_method || ""),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.booking_source")} />,
      dataIndex: "booking_source",
      key: "booking_source",
      width: 170,
      render: (v) => capitalizeFirstLetter(v) || "-",
      sorter: (a, b) => (a.booking_source || "").localeCompare(b.booking_source || ""),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.transaction_amount")} />,
      dataIndex: "amount",
      key: "amount",
      width: 200,
      render: (_, record) => formatAmount(record),
      align: "right",
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.taxes_and_fees")} />,
      dataIndex: "taxes_and_fees",
      key: "taxes_and_fees",
      width: 140,
      render: (v) => formatCurrency(v),
      align: "right",
      sorter: (a, b) => Number(a.taxes_and_fees || 0) - Number(b.taxes_and_fees || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: (
        <ColumnHeader
          label={t("accounting.details.columns.total_booking_amount")}
          tooltip={t("accounting.details.columns.total_booking_amount_tooltip")}
        />
      ),
      dataIndex: "booking_total_amount",
      key: "booking_total_amount",
      width: 230,
      render: (v) => formatCurrency(v),
      align: "right",
      sorter: (a, b) => Number(a.booking_total_amount || 0) - Number(b.booking_total_amount || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    }
  ]
}

function optionalSalesColumns(t) {
  return [
    {
      title: <ColumnHeader label={t("accounting.details.columns.base_cost")} tooltip={t("accounting.details.columns.base_cost_tooltip")} />,
      dataIndex: "base_cost",
      key: "base_cost",
      width: 140,
      render: (v) => formatCurrency(v),
      align: "right",
      sorter: (a, b) => Number(a.base_cost || 0) - Number(b.base_cost || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.first_experience_date")} />,
      dataIndex: "first_experience_date",
      key: "first_experience_date",
      width: 150,
      ellipsis: true,
      render: (d) => d || "-",
      sorter: (a, b) => new Date(a.first_experience_date || 0) - new Date(b.first_experience_date || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    },
    {
      title: <ColumnHeader label={t("accounting.details.columns.last_experience_date")} />,
      dataIndex: "last_experience_date",
      key: "last_experience_date",
      width: 150,
      ellipsis: true,
      render: (d) => d || "-",
      sorter: (a, b) => new Date(a.last_experience_date || 0) - new Date(b.last_experience_date || 0),
      showSorterTooltip: false,
      onHeaderCell: sortIconOnlyHeaderCell
    }
  ]
}

export function useDetailsColumns({ isSales, onEdit, onDelete }) {
  const { t } = useTranslation()
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => new Set(DEFAULT_VISIBLE_KEYS))

  const toggleColumn = useCallback((key, checked) => {
    if (LOCKED_COLUMN_KEYS.has(key)) return
    setVisibleColumnKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const expenseColumnsWithActions = useMemo(
    () => [
      {
        title: t("accounting.details.columns.date"),
        dataIndex: "date",
        key: "date",

        width: 120,
        render: (date) => date || "-",
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        sorter: (a, b) => new Date(a.date || 0) - new Date(b.date || 0),
        showSorterTooltip: false
      },
      {
        title: t("accounting.details.columns.merchant_name"),
        dataIndex: "displayed_name",
        key: "displayed_name",

        render: (v) => (v || "-").replaceAll("\n", " ").trim(),
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        sorter: (a, b) => (a.displayed_name || "").localeCompare(b.displayed_name || ""),
        showSorterTooltip: false
      },
      {
        title: t("accounting.details.columns.item_name"),
        key: "itemName",

        onCell: (record) => (record._isLastLineItem ? {} : { style: { borderBottom: "none" } }),
        render: (_, record) => (record._lineItem?.name || "-").replaceAll("\n", " ").trim()
      },
      {
        title: t("accounting.details.columns.category"),
        key: "category",

        onCell: (record) => (record._isLastLineItem ? {} : { style: { borderBottom: "none" } }),
        render: (_, record) => record._lineItem?.category?.name || "-"
      },
      {
        title: t("accounting.details.columns.amount_paid"),
        key: "amountPaid",

        align: "right",
        onCell: (record) => (record._isLastLineItem ? {} : { style: { borderBottom: "none" } }),
        render: (_, record) => {
          const amt = record._lineItem?.amount
          if (amt == null) return <span>-</span>
          return <span className={styles.refundAmount}>{`-${formatCurrency(amt)}`}</span>
        }
      },
      {
        title: t("accounting.details.columns.total_amount"),
        key: "totalAmount",

        align: "right",
        width: 130,
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        render: (_, record) => (
          <span className={styles.refundAmount}>{`-${record.formatted_amount || formatCurrency(Math.abs(record.amount || 0))}`}</span>
        )
      },
      {
        key: "actions",
        width: 48,
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                { key: "edit", label: t("accounting.details.edit") },
                { key: "delete", label: t("accounting.details.delete"), danger: true }
              ],
              onClick: ({ key }) => {
                if (key === "edit") onEdit(record)
                if (key === "delete") onDelete(record)
              }
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      }
    ],
    [t, onEdit, onDelete]
  )

  // Build the ordered list of all sales columns merging base + optional
  const allColsOrdered = useMemo(() => {
    const base = baseSalesColumns(t)
    const optional = optionalSalesColumns(t)
    const byKey = Object.fromEntries([...base, ...optional].map((c) => [c.key, c]))
    return ALL_SALES_COLUMN_KEYS.map((k) => byKey[k]).filter(Boolean)
  }, [t])

  // Columns that are NOT locked (can be toggled)
  const toggleableCols = useMemo(() => allColsOrdered.filter((col) => !LOCKED_COLUMN_KEYS.has(col.key)), [allColsOrdered])

  const toggleAllOptional = useCallback(
    (e) =>
      setVisibleColumnKeys((prev) => {
        const next = new Set(prev)
        toggleableCols.forEach((col) => (e.target.checked ? next.add(col.key) : next.delete(col.key)))
        return next
      }),
    [toggleableCols]
  )

  const addColumnsContent = useMemo(() => {
    const allToggleableVisible = toggleableCols.every((col) => visibleColumnKeys.has(col.key))
    const someToggleableVisible = toggleableCols.some((col) => visibleColumnKeys.has(col.key))

    return (
      <div className={styles.columnPickerMenu}>
        <Checkbox
          checked={allToggleableVisible}
          indeterminate={someToggleableVisible && !allToggleableVisible}
          onChange={toggleAllOptional}
        >
          {t("accounting.details.filters.all")}
        </Checkbox>
        <Divider className={styles.columnPickerDivider} />
        {allColsOrdered.map((col) => {
          const locked = LOCKED_COLUMN_KEYS.has(col.key)
          return (
            <div key={col.key} className={styles.columnPickerItem}>
              <Checkbox
                checked={visibleColumnKeys.has(col.key)}
                disabled={locked}
                onChange={(e) => toggleColumn(col.key, e.target.checked)}
              >
                {/* Render plain label text in the checkbox, not the React header node */}
                {getColumnLabel(col.key, t)}
              </Checkbox>
            </div>
          )
        })}
      </div>
    )
  }, [t, visibleColumnKeys, allColsOrdered, toggleableCols, toggleAllOptional, toggleColumn])

  const columns = useMemo(() => {
    if (!isSales) return expenseColumnsWithActions
    const visible = allColsOrdered.filter((col) => visibleColumnKeys.has(col.key))
    const addCol = {
      key: "add-columns",
      width: 48,
      fixed: "right",
      title: (
        <Popover content={addColumnsContent} title={t("accounting.details.filters.add_columns")} trigger="click" placement="bottomRight">
          <Button type="text" icon={<PlusOutlined />} size="small" />
        </Popover>
      ),
      render: () => null
    }
    return [...visible, addCol]
  }, [isSales, visibleColumnKeys, addColumnsContent, allColsOrdered, expenseColumnsWithActions, t])

  return { columns }
}

function getColumnLabel(key, t) {
  const map = {
    date: t("accounting.details.columns.transaction_date"),
    transaction_id: t("accounting.details.columns.transaction_id"),
    booking_number: t("accounting.details.columns.booking_id"),
    displayed_name: t("accounting.details.columns.client_name"),
    payment_method: t("accounting.details.columns.payment_method"),
    booking_source: t("accounting.details.columns.booking_source"),
    amount: t("accounting.details.columns.transaction_amount"),
    taxes_and_fees: t("accounting.details.columns.taxes_and_fees"),
    booking_total_amount: t("accounting.details.columns.total_booking_amount"),
    base_cost: t("accounting.details.columns.base_cost"),
    first_experience_date: t("accounting.details.columns.first_experience_date"),
    last_experience_date: t("accounting.details.columns.last_experience_date")
  }
  return map[key] || key
}
